import { useState, useEffect } from "react";
import { api, formatApiError, imgUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, X, Image as ImageIcon, Star, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { Field } from "./shared";

const PRESS_BLANK = { source: "", title: "", href: "", image: "", order: 0, featured: false };
const PRESS_INPUT_CLS =
  "bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0";

export function PressPanel() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(PRESS_BLANK);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const handleDrop = async (toIdx) => {
    if (dragIdx === null || dragIdx === toIdx) { setDragIdx(null); return; }
    const next = [...items];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(toIdx, 0, moved);
    setItems(next);
    setDragIdx(null);
    try {
      await api.post("/admin/press/reorder", { ids: next.map((it) => it.id) });
      toast.success("Order saved — controls EPK highlight order too");
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Reorder failed");
      await load();
    }
  };

  const load = async () => {
    try {
      const r = await api.get("/admin/press");
      setItems(r.data.items || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not load press items");
    }
  };
  useEffect(() => {
    const t = setTimeout(load, 0);
    return () => clearTimeout(t);
  }, []);

  const startCreate = () => { setForm(PRESS_BLANK); setEditing(null); setShowForm(true); };
  const startEdit = (it) => {
    setForm({
      ...PRESS_BLANK,
      ...it,
      source: it.source || "",
      title: it.title || "",
      href: it.href || "",
      image: it.image || "",
      order: it.order ?? 0,
      featured: !!it.featured,
    });
    setEditing(it.id);
    setShowForm(true);
  };
  const cancel = () => { setShowForm(false); setEditing(null); setForm(PRESS_BLANK); };
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.source.trim()) { toast.error("Source / outlet is required."); return; }
    setSaving(true);
    try {
      const payload = {
        source: form.source.trim(),
        title: form.title?.trim() || null,
        href: form.href?.trim() || null,
        image: form.image?.trim() || null,
        order: parseInt(form.order, 10) || 0,
        featured: !!form.featured,
      };
      if (editing) {
        await api.put(`/admin/press/${editing}`, payload);
        toast.success("Press item updated");
      } else {
        await api.post("/admin/press", payload);
        toast.success("Press item added");
      }
      cancel();
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this press item permanently?")) return;
    try {
      await api.delete(`/admin/press/${id}`);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleFeature = async (it) => {
    const next = !it.featured;
    setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, featured: next } : x)));
    try {
      await api.patch(`/admin/press/${it.id}/feature`, { featured: next });
      toast.success(next ? "Starred — will show as an EPK highlight" : "Removed from EPK highlights");
    } catch (err) {
      setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, featured: !next } : x)));
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not update highlight");
    }
  };
  const featuredCount = items.filter((x) => x.featured).length;

  return (
    <div data-testid="press-panel">
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-white/55">
          {items.length} press item{items.length === 1 ? "" : "s"} · shown on the EPK page · ⭐ {featuredCount} highlighted
        </p>
        <Button
          onClick={() => (showForm ? cancel() : startCreate())}
          className="btn-gold rounded-none px-6 py-4 text-xs"
          data-testid="admin-new-press"
        >
          {showForm ? <X size={14} /> : <Plus size={14} />}
          {showForm ? "Cancel" : "New Press Item"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={save} className="surface p-8 mb-10 grid sm:grid-cols-2 gap-6" data-testid="press-form">
          <Field label="Source / Outlet *">
            <Input value={form.source} onChange={update("source")} placeholder="Influential Women" className={PRESS_INPUT_CLS} data-testid="press-source" />
          </Field>
          <Field label="Display Order">
            <Input type="number" value={form.order} onChange={update("order")} className={PRESS_INPUT_CLS} data-testid="press-order" />
          </Field>
          <Field label="Headline / Title (optional)" full>
            <Input value={form.title} onChange={update("title")} className={PRESS_INPUT_CLS} data-testid="press-title" />
          </Field>
          <Field label="Link URL (optional)" full>
            <Input value={form.href} onChange={update("href")} placeholder="https://…" className={PRESS_INPUT_CLS} data-testid="press-href" />
          </Field>
          <Field label="Image URL (paste a link, or save then upload a file below)" full>
            <Input value={form.image} onChange={update("image")} placeholder="https://….png" className={PRESS_INPUT_CLS} data-testid="press-image-url" />
          </Field>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={saving} className="btn-gold rounded-none px-8 py-4 text-xs" data-testid="press-save">
              {saving ? "Saving…" : editing ? "Update Item" : "Add Press Item"}
            </Button>
          </div>
        </form>
      )}

      {items.length === 0 ? (
        <p className="text-white/45 py-6">No press items yet. Add your first feature above.</p>
      ) : (
        <div className="space-y-3">
          {items.map((it, idx) => (
            <div
              key={it.id}
              draggable
              onDragStart={() => setDragIdx(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(idx)}
              onDragEnd={() => setDragIdx(null)}
              className={`surface p-4 flex items-center gap-4 transition-opacity ${dragIdx === idx ? "opacity-40" : ""}`}
              data-testid={`press-row-${it.id}`}
            >
              <span className="shrink-0 text-white/25 cursor-grab active:cursor-grabbing" title="Drag to reorder (also sets EPK highlight order)" data-testid={`press-drag-${it.id}`}>
                <GripVertical size={16} />
              </span>
              <PressImageUploader item={it} reload={load} />
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg tracking-tighter truncate">{it.source}</p>
                {it.title && <p className="text-sm text-white/55 truncate">{it.title}</p>}
                {it.href && (
                  <a href={it.href} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[color:var(--gold)] truncate inline-block max-w-full">
                    {it.href}
                  </a>
                )}
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <button
                  onClick={() => toggleFeature(it)}
                  className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] transition-colors ${it.featured ? "text-[color:var(--gold)]" : "text-white/40 hover:text-[color:var(--gold)]"}`}
                  title={it.featured ? "Featured as an EPK highlight — click to remove" : "Star to feature as an EPK highlight"}
                  data-testid={`press-feature-${it.id}`}
                >
                  <Star size={14} fill={it.featured ? "currentColor" : "none"} />
                  {it.featured ? "Highlighted" : "Highlight"}
                </button>
                <button onClick={() => startEdit(it)} className="text-[10px] uppercase tracking-[0.2em] text-white/55 hover:text-[color:var(--gold)]" data-testid={`press-edit-${it.id}`}>
                  Edit
                </button>
                <button onClick={() => remove(it.id)} className="text-[10px] uppercase tracking-[0.2em] text-white/45 hover:text-red-400" data-testid={`press-delete-${it.id}`}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PressImageUploader({ item, reload }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/press/${item.id}/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Image uploaded.");
      if (reload) await reload();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Image upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <label
      className="relative block w-16 h-16 border border-white/10 cursor-pointer overflow-hidden group/press shrink-0"
      data-testid={`press-image-upload-${item.id}`}
    >
      {item.image ? (
        <img src={imgUrl(item.image)} alt="Press" className="w-full h-full object-cover" />
      ) : (
        <span className="w-full h-full flex flex-col items-center justify-center gap-1 text-[8px] uppercase tracking-[0.15em] text-white/40">
          <ImageIcon size={16} /> Img
        </span>
      )}
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        disabled={busy}
        onChange={(e) => upload(e.target.files?.[0])}
        className="hidden"
      />
      <span className="absolute inset-0 bg-black/55 opacity-0 group-hover/press:opacity-100 flex items-center justify-center text-[8px] uppercase tracking-[0.15em] text-[color:var(--gold)] transition-opacity">
        {busy ? "…" : item.image ? "Replace" : "Upload"}
      </span>
    </label>
  );
}
